import React, { useEffect, useState } from 'react';
import { metaApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Modal, { useModal } from '../components/Modal';
import LoadingOverlay from '../components/LoadingOverlay';

interface Page {
  id: string; name: string;
  instagram_business_account?: { id: string; username: string };
}

export default function ConnectMetaPage() {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { modal, show, close } = useModal();
  const [pages, setPages] = useState<Page[]>([]);
  const [tempToken, setTempToken] = useState('');
  const [loading, setLoading] = useState<string | null>(null);
  const [selectedPage, setSelectedPage] = useState<string | null>(null);
  const [igLoading, setIgLoading] = useState(false);
  const [fbLoading, setFbLoading] = useState(false);
  const [adsAccess, setAdsAccess] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user?.has_facebook) { setAdsAccess(null); return; }
    metaApi.status().then(r => setAdsAccess(r.data?.ads_access ?? null)).catch(() => setAdsAccess(null));
  }, [user?.has_facebook, user?.facebook_page_name]);

  useEffect(() => {
    // Ritorno da Facebook senza collegamento (annullato, sessione scaduta, nessuna pagina)
    const metaError = searchParams.get('meta_error');
    if (metaError) {
      const msgs: Record<string, string> = {
        denied: 'Non hai autorizzato il collegamento con Facebook. Riprova quando vuoi.',
        expired: 'Sessione scaduta. Riprova a collegare Facebook.',
        token_failed: 'Impossibile completare il collegamento con Facebook. Riprova.',
        no_pages: 'Nessuna Pagina Facebook trovata sul tuo account. Per usare Molti Like serve una Pagina Facebook.',
      };
      show({ title: 'Collegamento non riuscito', message: msgs[metaError] || 'Errore durante il collegamento con Facebook. Riprova.', type: 'error', onConfirm: close });
    }
    const metaSession = searchParams.get('meta_session');
    if (metaSession) {
      setTempToken(metaSession);
      metaApi.getPageSession(metaSession)
        .then(res => setPages(res.data.pages || []))
        .catch(() => show({ title: 'Sessione scaduta', message: 'La sessione di connessione è scaduta. Riprova a collegare il tuo account.', type: 'error', onConfirm: close }));
    }
    const igConnected = searchParams.get('ig_connected');
    const igError = searchParams.get('ig_error');
    if (igConnected === '1') {
      refreshUser();
    }
    if (igError) {
      const msgs: Record<string, string> = {
        denied: 'Hai rifiutato l\'autorizzazione Instagram.',
        expired: 'Sessione scaduta. Riprova.',
        token_failed: 'Impossibile ottenere il token Instagram. Riprova.',
      };
      show({ title: 'Errore connessione Instagram', message: msgs[igError] || 'Errore durante la connessione. Riprova.', type: 'error', onConfirm: close });
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const startAuth = async () => {
    // Feedback immediato: la chiamata può durare qualche secondo e senza
    // spinner il pulsante sembrava non fare nulla.
    setFbLoading(true);
    try {
      const r = await metaApi.getAuthUrl();
      window.location.href = r.data.url;
    } catch {
      show({ title: 'Errore connessione', message: 'Impossibile avviare la connessione. Riprova.', type: 'error', onConfirm: close });
      setFbLoading(false);
    }
  };

  const startIgAuth = async () => {
    setIgLoading(true);
    try {
      const r = await metaApi.getIgAuthUrl();
      window.location.href = r.data.url;
    } catch {
      show({ title: 'Errore connessione', message: 'Impossibile avviare la connessione Instagram. Riprova.', type: 'error', onConfirm: close });
      setIgLoading(false);
    }
  };

  const selectPage = async (page: Page) => {
    setSelectedPage(page.id);
    setLoading(page.id);
    try {
      await metaApi.connectPage({
        page_id: page.id,
        session_id: tempToken,
      });
      await refreshUser();
      navigate('/');
    } catch (err: any) {
      show({
        title: 'Errore collegamento',
        message: err.response?.data?.error || 'Impossibile collegare la pagina. Riprova.',
        type: 'error',
        onConfirm: close,
      });
    } finally {
      setLoading(null);
      setSelectedPage(null);
    }
  };

  const disconnectMeta = () => {
    show({
      title: 'Scollega account',
      message: 'Vuoi scollegare Facebook e Instagram? Potrai ricollegarli in seguito.',
      type: 'confirm',
      confirmText: 'Scollega',
      cancelText: 'Annulla',
      onConfirm: async () => {
        close();
        await metaApi.disconnect();
        await refreshUser();
      },
      onCancel: close,
    });
  };

  // Caricamento a tutto schermo per ogni attesa del server: apertura di
  // Facebook/Instagram, recupero delle pagine al ritorno, collegamento.
  const overlay =
    fbLoading ? <LoadingOverlay title="Apertura di Facebook..." subtitle="Ti portiamo al login per autorizzare Molti Like" /> :
    igLoading ? <LoadingOverlay title="Apertura di Instagram..." subtitle="Ti portiamo al login per autorizzare Molti Like" /> :
    loading   ? <LoadingOverlay title="Collegamento in corso..." subtitle="Salviamo la pagina scelta" icon="link" /> :
    (searchParams.get('meta_session') && pages.length === 0 && !modal) ? <LoadingOverlay title="Recupero delle tue pagine..." subtitle="Un momento, stiamo leggendo i dati da Facebook" /> :
    null;

  // Already connected (Facebook+Instagram or Instagram-only)
  if (user?.has_facebook || user?.has_instagram) {
    return (
      <div className="page">
        {overlay}
        {modal && (
          <Modal isOpen title={modal.title} message={modal.message} type={modal.type}
            confirmText={modal.confirmText} cancelText={modal.cancelText}
            onConfirm={modal.onConfirm} onCancel={modal.onCancel ?? close} />
        )}

        <div className="top-nav">
          <button className="back-btn" onClick={() => navigate(-1)}>
            <span className="mi mi-20">arrow_back</span> Indietro
          </button>
          <span className="nav-title">Social collegati</span>
        </div>

        <div className="alert success">
          <span className="mi mi-18">check_circle</span>
          <span>Connessione attiva — i tuoi social sono collegati correttamente.</span>
        </div>

        <div className="card">
          <div className="data-rows">
            {user?.has_facebook && (
              <div className="data-row">
                <span className="data-key" style={{ display: 'flex', alignItems: 'center', gap: 5, paddingTop: 0 }}>
                  <span style={{ fontWeight: 900, color: '#1877F2', fontSize: 15 }}>f</span>Facebook
                </span>
                <span className="data-val">{user.facebook_page_name}</span>
              </div>
            )}
            {user?.has_instagram && (
              <div className="data-row" style={!user?.has_facebook ? { borderBottom: 'none' } : undefined}>
                <span className="data-key" style={{ display: 'flex', alignItems: 'center', gap: 5, paddingTop: 0 }}>
                  <span className="mi mi-16" style={{ color: '#E1306C' }}>photo_camera</span>Instagram
                </span>
                <span className="data-val">{user.instagram_username ? `@${user.instagram_username}` : '—'}</span>
              </div>
            )}
            {user.has_facebook && adsAccess !== null && (
                  <div className="data-row" style={{ borderBottom: 'none' }}>
                    <span className="data-key" style={{ display: 'flex', alignItems: 'center', gap: 5, paddingTop: 0 }}>
                      <span className="mi mi-16" style={{ color: adsAccess ? 'var(--success)' : 'var(--warning)' }}>{adsAccess ? 'verified' : 'hourglass_empty'}</span>Promozione
                    </span>
                    <span className="data-val" style={{ color: adsAccess ? 'var(--success)' : 'var(--warning)' }}>{adsAccess ? 'Abilitata' : 'Non ancora abilitata'}</span>
                  </div>
                )}
          </div>
          <div style={{ marginTop: 14, display: 'flex', gap: 6 }}>
            <span className="badge badge-success" style={{ padding: '4px 10px' }}>
              <span className="mi mi-16">check_circle</span>&nbsp;Attivo
            </span>
          </div>
        </div>

        <button className="btn btn-danger" onClick={disconnectMeta}>
          <span className="mi mi-20">link_off</span> Scollega account
        </button>
      </div>
    );
  }

  // Page selection
  if (pages.length > 0) {
    return (
      <div className="page">
        {overlay}
        {modal && (
          <Modal isOpen title={modal.title} message={modal.message} type={modal.type}
            confirmText={modal.confirmText} cancelText={modal.cancelText}
            onConfirm={modal.onConfirm} onCancel={modal.onCancel ?? close} />
        )}

        <div className="top-nav">
          <button className="back-btn" onClick={() => navigate(-1)}>
            <span className="mi mi-20">arrow_back</span> Indietro
          </button>
          <span className="nav-title">Collega Social</span>
        </div>

        <div>
          <h2 style={{ marginBottom: 6 }}>Seleziona la tua pagina</h2>
          <p className="sub">Scegli la pagina Facebook da collegare all'app</p>
        </div>

        {pages.map(page => (
          <div
            key={page.id}
            className={`page-card${selectedPage === page.id ? ' selected' : ''}`}
            onClick={() => loading ? null : selectPage(page)}
          >
            <div className="page-avatar">f</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-1)' }}>{page.name}</div>
              {page.instagram_business_account ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 4 }}>
                  <span className="mi mi-16" style={{ color: '#E1306C' }}>photo_camera</span>
                  <span style={{ fontSize: 12, color: 'var(--text-2)' }}>@{page.instagram_business_account.username}</span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 4 }}>
                  <span className="mi mi-16" style={{ color: 'var(--text-3)' }}>link_off</span>
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Nessun account IG collegato</span>
                </div>
              )}
              {loading === page.id && (
                <div style={{ fontSize: 12, color: 'var(--primary)', marginTop: 4 }}>Collegamento in corso...</div>
              )}
            </div>
            <button
              className={`btn ${page.instagram_business_account ? 'btn-primary' : 'btn-secondary'}`}
              style={{ width: 'auto', padding: '9px 16px', fontSize: 13 }}
              disabled={!!loading}
            >
              Seleziona
            </button>
          </div>
        ))}

        <div className="alert warning">
          <span className="mi mi-18">info</span>
          <span>Per pubblicare su Instagram, la pagina Facebook deve avere un account Instagram Business collegato.</span>
        </div>
      </div>
    );
  }

  // Initial state
  return (
    <div className="page">
        {overlay}
      {modal && (
        <Modal isOpen title={modal.title} message={modal.message} type={modal.type}
          confirmText={modal.confirmText} cancelText={modal.cancelText}
          onConfirm={modal.onConfirm} onCancel={modal.onCancel ?? close} />
      )}

      <div style={{ textAlign: 'center', paddingTop: 20 }}>
        <span className="mi" style={{ fontSize: 52, color: 'var(--primary)', display: 'block', marginBottom: 16 }}>smartphone</span>
        <h1 style={{ marginBottom: 8 }}>Collega i tuoi social</h1>
        <p className="sub" style={{ maxWidth: 300, margin: '0 auto' }}>
          Connetti i tuoi account social per pubblicare post e attivare la promozione automatica.
        </p>
      </div>

      {/* Facebook + Instagram card */}
      <div className="connect-card">
        <div className="connect-icon" style={{ background: 'rgba(24,119,242,0.15)' }}>
          <span style={{ fontSize: 32, fontWeight: 900, color: '#1877F2' }}>f</span>
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Facebook & Instagram</div>
          <p className="sub" style={{ fontSize: 12 }}>
            Hai una pagina Facebook con Instagram Business collegato.
          </p>
        </div>
        <button className="btn btn-facebook" onClick={startAuth} disabled={fbLoading}>
          {fbLoading
            ? <><span className="mi mi-18 spin">sync</span> Apertura di Facebook...</>
            : <><span style={{ fontSize: 18, fontWeight: 800 }}>f</span> Collega con Facebook</>}
        </button>
      </div>

      {/* Instagram only card */}
      <div className="connect-card">
        <div className="connect-icon" style={{ background: 'rgba(225,48,108,0.12)' }}>
          <span className="mi" style={{ fontSize: 28, color: '#E1306C' }}>photo_camera</span>
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Solo Instagram</div>
          <p className="sub" style={{ fontSize: 12 }}>
            Hai solo un profilo Instagram Professional o Business.
          </p>
        </div>
        <button className="btn btn-instagram" onClick={startIgAuth} disabled={igLoading}>
          <span className="mi mi-18">photo_camera</span>
          {igLoading ? 'Connessione...' : 'Collega Instagram'}
        </button>
      </div>

      <div className="card">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {['Pubblica su Facebook e Instagram in un click', 'Promozione automatica dei tuoi contenuti', 'Visibilità attiva 24/7'].map((txt, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="mi mi-18" style={{ color: 'var(--success)' }}>check_circle</span>
              <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{txt}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
